import Vue from 'vue'
import axios from 'axios'
import { apolloClient } from '@/apollo'
import gql from 'graphql-tag'
import config from '@/config'

export const fetchCatalogsQuery = gql`
  query SearchCatalogs($organization_id: uuid!) {
    catalog(where: { organization_id: { _eq: $organization_id } }) {
      id
      name
      is_default
      is_active
      type {
        id
        description
      }
      taxonomy {
        id
        name
      }
      organization {
        id
        name
      }
      items_aggregate(where: { deleted_at: { _is_null: true } }) {
        aggregate {
          count
        }
      }
      contract_start_date
      contract_end_date
      notify
    }
  }
`

export const fetchCatalogQuery = gql`
  query SearchCatalog($catalog_id: uuid!) {
    catalog(where: { id: { _eq: $catalog_id } }) {
      id
      name
      organization_id
      is_default
      is_active
      taxonomy_id
      catalog_type_id
      items_aggregate(where: { deleted_at: { _is_null: true } }) {
        aggregate {
          count
        }
      }
      type {
        id
        description
      }
      organization {
        id
        name
      }
      contract_start_date
      contract_end_date
      observations
      contracted_value
      notify
    }
  }
`

export const createCatalogMutation = gql`
  mutation CreateCatalog($data: [catalog_insert_input!]!) {
    insert_catalog(objects: $data) {
      affected_rows
      returning {
        id
        name
        catalog_type_id
        organization_id
        is_default
        is_active
        taxonomy_id
        contract_start_date
        contract_end_date
        observations
        contracted_value
        notify
      }
    }
  }
`

export const updateCatalogMutation = gql`
  mutation UpdateCatalog($catalogId: uuid!, $data: catalog_set_input!) {
    update_catalog(where: { id: { _eq: $catalogId } }, _set: $data) {
      affected_rows
      returning {
        id
        name
        organization_id
        catalog_type_id
        is_default
        is_active
        taxonomy_id
        contract_start_date
        contract_end_date
        observations
        contracted_value
        notify
      }
    }
  }
`

export const fetchLastCatalogUploadQuery = gql`
  query GetLastCatalogUpload($catalog_id: uuid!) {
    import_catalog(
      where: { catalog_id: { _eq: $catalog_id } }
      order_by: { created_at: desc }
      limit: 1
    ) {
      id
      created_at
      catalog_id
      file_name
      file_url
      total_count
      total_insert
      total_update
      total_delete
      total_active
      batches {
        id
        status
        from_index
        to_index
        total_count
        insert_count
        updated_at
        delete_count
        active_count
      }
    }
  }
`

export const fetchCatalogTypeQuery = gql`
  query GetCatalogTypes {
    catalog_type {
      id
      description
    }
  }
`

export const uploadCatalogMutation = gql`
  mutation InsertImportCatalogRequest($data: [import_catalog_insert_input!]!) {
    insert_import_catalog(objects: $data) {
      returning {
        id
        catalog_id
        file_url
        file_name
      }
    }
  }
`

export const setOrganizationRequiresTermsAcceptance = gql`
  mutation setOrganizationRequiresTermsAcceptance(
    $id: uuid!
    $requires_terms_acceptance: Boolean!
  ) {
    updatedOrg: update_organization_by_pk(
      pk_columns: { id: $id }
      _set: { requires_terms_acceptance: $requires_terms_acceptance }
    ) {
      id
    }
  }
`

const mapperQuery = (filterMap, filters) => {
  return filterMap.reduce((acc, { key, expression, isRaw, validate }) => {
    const value = filters[key]
    const isValid =
      typeof validate === 'function'
        ? validate(value)
        : value !== undefined && value !== null

    if (!isValid) return acc

    if (isRaw) {
      acc.push(`${key}: $${key}`)
    } else {
      acc.push(`${key}: { ${expression}: $${key} }`)
    }

    return acc
  }, [])
}

export const fetchCatalogsByOrg = filters => {
  // TODO: agregar filtros de manera dinámica para aquellos que se les pasa una expresión en vez del valor

  const isNullvalidate = value => ![null, undefined].includes(value)
  const filterMap = [
    { key: 'country', expression: '_eq' },
    { key: 'tax_id', expression: '_eq' },
    { key: 'plan_id', expression: '_eq' },
    { key: 'organization_name', expression: '_ilike' },
    { key: 'is_active', expression: '_eq', validate: isNullvalidate },
    { key: 'is_default', expression: '_eq', validate: isNullvalidate },
    { key: 'total_items', isRaw: true },
    { key: 'catalog_type_id', expression: '_eq' },
    { key: 'contracted_value', isRaw: true },
    { key: 'contract_end_date', isRaw: true },
    { key: 'contract_start_date', isRaw: true }
  ]
  const whereConditions = mapperQuery(filterMap, filters)
  const whereFilterStr = whereConditions.length
    ? `where: { ${whereConditions.join('\n')} }`
    : ''

  const aggregateBlock = whereConditions.length
    ? `catalog_admin_view_aggregate(${whereFilterStr})`
    : `catalog_admin_view_aggregate`

  return gql`
    query fetchCatalogsByOrg(
      $offset: Int = 0
      $limit: Int = 10
      $tax_id: String
      $organization_name: String
      $country: String
      $plan_id: uuid
      $is_active: Boolean
      $is_default: Boolean
      $total_items: bigint_comparison_exp
      $catalog_type_id: Int
      $contracted_value: float8_comparison_exp
      $contract_end_date: timestamp_comparison_exp
      $contract_start_date: timestamp_comparison_exp
    ) {
      catalog_admin_view(
        offset: $offset
        limit: $limit
        order_by: { organization_name: asc }
        ${whereFilterStr}
      ) {
        catalog_name
        catalog_type_description
        catalog_type_id
        contract_end_date
        contracted_value
        contract_start_date
        country
        id
        is_active
        is_default
        notify
        organization_id
        organization_name
        tax_id
        taxonomy_id
        taxonomy_name
        total_items
      }
      ${aggregateBlock} {
        aggregate {
          count
        }
      }
    }
  `
}

export const countriesInCatalogs = gql`
  query countriesInCatalogs {
    catalog_admin_view(distinct_on: country) {
      country
    }
  }
`

const catalog = {
  namespaced: true,
  state: {
    timestamptz: new Date().toISOString(),
    catalog: {},
    catalog_upload: null,
    catalogs: [],
    catalog_types: [],
    validatorResult: {
      name: '',
      totalItems: 0,
      totalSuccessItems: 0,
      totalErrorItems: 0,
      errors: []
    },
    list_catalogs: [],
    catalogCountries: [],
    catalogsCount: 0
  },
  actions: {
    init ({ state }) {
      try {
        Vue.set(state, 'catalog', {})
        Vue.set(state, 'validatorResult', {
          name: '',
          totalItems: 0,
          totalSuccessItems: 0,
          totalErrorItems: 0,
          errors: []
        })
      } catch (error) {
        throw new Error(error)
      }
    },
    async fetchAll ({ state, commit, dispatch }, organizationId) {
      try {
        let requestQuery = {
          fetchPolicy: 'network-only',
          query: fetchCatalogsQuery,
          variables: {
            organization_id: organizationId
          }
        }

        const result = await apolloClient.query(requestQuery)
        if (result && result.data) {
          commit('SET_CATALOGS', result.data.catalog)
          if (state.catalogCreatedOrModified != null) { dispatch('VERIFY_ORGANIZATION_TERMS_ACCEPTANCE') }
        }
      } catch (err) {
        window.console.error(err)
      }
    },
    async get ({ commit }, catalogId) {
      try {
        const requestQuery = {
          fetchPolicy: 'network-only',
          query: fetchCatalogQuery,
          variables: {
            catalog_id: catalogId
          }
        }

        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          commit('SET_CATALOG', response.data.catalog[0])
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async create ({ state, commit }, catalog) {
      try {
        const insertMutation = {
          mutation: createCatalogMutation,
          variables: {
            data: {
              name: catalog.name,
              catalog_type_id: catalog.catalog_type_id,
              organization_id: catalog.organization_id,
              is_default: catalog.is_default,
              is_active: catalog.is_active,
              taxonomy_id: catalog.taxonomy_id,
              contract_start_date: catalog.contract_start_date,
              contract_end_date: catalog.contract_end_date,
              observations: catalog.observations,
              contracted_value: catalog.contracted_value,
              notify: catalog.notify
            }
          }
        }

        const result = await apolloClient.mutate(insertMutation)
        if (
          result &&
          result.data &&
          result.data.insert_catalog.affected_rows === 1
        ) {
          commit('SET_CATALOG', result.data.insert_catalog.returning[0])
          state.catalogCreatedOrModified =
            result.data.insert_catalog.returning[0]
        }

        return result
      } catch (error) {
        window.console.error(error)
        throw new Error(error)
      }
    },
    async update ({ state, commit }, catalog) {
      try {
        const updateMutation = {
          mutation: updateCatalogMutation,
          variables: {
            catalogId: catalog.id,
            data: {
              name: catalog.name,
              catalog_type_id: catalog.catalog_type_id,
              organization_id: catalog.organization_id,
              is_default: catalog.is_default,
              is_active: catalog.is_active,
              taxonomy_id: catalog.taxonomy_id,
              contract_start_date: catalog.contract_start_date,
              contract_end_date: catalog.contract_end_date,
              observations: catalog.observations,
              contracted_value: catalog.contracted_value,
              notify: catalog.notify
            }
          }
        }
        const result = await apolloClient.mutate(updateMutation)
        if (result && result.data) {
          commit('SET_CATALOG', result.data.update_catalog.returning[0])
          state.catalogCreatedOrModified =
            result.data.update_catalog.returning[0]
        }

        return result
      } catch (error) {
        throw new Error(error)
      }
    },
    async uploadCatalog ({ commit }, uploadData) {
      try {
        var uploadMutation = {
          mutation: uploadCatalogMutation,
          variables: {
            data: {
              status: 'PENDING',
              catalog_id: uploadData.catalog_id,
              total_count: uploadData.total_count,
              file_url: uploadData.file_url,
              file_name: uploadData.file_name,
              imported_at: new Date().toISOString()
            }
          }
        }

        const result = await apolloClient.mutate(uploadMutation)
        if (result && result.data) {
          commit(
            'SET_CATALOG_UPLOAD',
            result.data.insert_import_catalog.returning[0]
          )
          commit('SET_VALIDATOR_RESULTS', {
            name: '',
            totalItems: 0,
            totalSuccessItems: 0,
            totalErrorItems: 0,
            errors: []
          })
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async validatorCatalog ({ commit }, formData) {
      axios
        .post(
          `${config.ENDPOINTS.CAT4MRO_APIHTTP}/api/catalog/validator`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then(function (response) {
          commit('SET_VALIDATOR_RESULTS', response.data)
        })
        .catch(function (e) {
          console.error('UPLOAD FAILURE!!')
          Promise.reject(e)
        })
    },
    async getLastUploadCatalog ({ commit }, catalogId) {
      try {
        const requestQuery = {
          query: fetchLastCatalogUploadQuery,
          variables: {
            catalog_id: catalogId
          }
        }

        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          commit('SET_CATALOG_UPLOAD', response.data.import_catalog[0])
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    async getCatalogTypes ({ commit }, catalogId) {
      try {
        const requestQuery = {
          query: fetchCatalogTypeQuery
        }

        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          commit('SET_CATALOG_TYPES', response.data.catalog_type)
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    VERIFY_ORGANIZATION_TERMS_ACCEPTANCE ({ state, dispatch }) {
      try {
        if (
          state.catalogs == null ||
          !(state.catalogs.length > 0) ||
          state.catalog_types == null ||
          !(state.catalog_types.length > 0)
        ) {
          throw new Error(
            'Catalog or catalog types probably is undefined (VERIFY_ORGANIZATION_TERMS_ACCEPTANCE)\n',
            state.catalogCreatedOrModified.organization_id
          ) 
        }
        const PUBLIC_CATALOG_ID = 1 // The ID of Public Catalog is the number 1
        if (state.catalogs.length === 1) {
          if (
            state.catalogCreatedOrModified.catalog_type_id !== PUBLIC_CATALOG_ID
          ) {
            dispatch('uploadOrganizationRequiresTermsAcceptance', {
              organizationID: state.catalogCreatedOrModified.organization_id,
              isRequired: false
            })
            state.catalogCreatedOrModified = null
            return
          }
          dispatch('uploadOrganizationRequiresTermsAcceptance', {
            organizationID: state.catalogCreatedOrModified.organization_id,
            isRequired: true
          })
          state.catalogCreatedOrModified = null
          return
        }
        const publicCatalog = state.catalog_types.find(
          catalog => catalog.id === PUBLIC_CATALOG_ID
        )
        if (publicCatalog == null) {
          throw new Error(
            'Public Catalog not found (VERIFY_ORGANIZATION_TERMS_ACCEPTANCE)\n',
            state.catalogCreatedOrModified.organization_id
          ) 
        }
        const hasPublicCatalog = state.catalogs.find(
          catalog =>
            catalog.id !== state.catalogCreatedOrModified.id &&
            catalog.type != null &&
            catalog.type.description === publicCatalog.description
        )
        if (hasPublicCatalog) {
          state.catalogCreatedOrModified = null
          return
        }
        dispatch('uploadOrganizationRequiresTermsAcceptance', {
          organizationID: state.catalogCreatedOrModified.organization_id,
          isRequired: true
        })
        state.catalogCreatedOrModified = null
      } catch (error) {
        window.console.error(
          'error in VERIFY_ORGANIZATION_TERMS_ACCEPTANCE:',
          error
        )
        state.catalogCreatedOrModified = null
      }
    },
    async uploadOrganizationRequiresTermsAcceptance (
      { commit },
      { organizationID, isRequired }
    ) {
      try {
        const uploadMutation = {
          mutation: setOrganizationRequiresTermsAcceptance,
          variables: {
            id: organizationID,
            requires_terms_acceptance: isRequired
          }
        }
        const result = await apolloClient.mutate(uploadMutation)
        if (result && result.data) {
          return result.data.updatedOrg.id
        }
      } catch (error) {
        window.console.error(error)
      }
    },
    async getCatByOrg ({ commit, state }, variables) {
      try {
        const dataVariables = JSON.parse(JSON.stringify(variables))

        delete dataVariables.targetSupplierOrg
        // delete dataVariables.plan_id

        Object.keys(dataVariables).forEach(key => {
          if ([null, undefined].includes(dataVariables[key])) {
            delete dataVariables[key]
          }
        })

        

        const requestQuery = {
          fetchPolicy: 'network-only',
          variables: dataVariables,
          query: fetchCatalogsByOrg(dataVariables)
        }
        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          commit('SET_LIST_CATALOG_BY_ORG', response.data.catalog_admin_view)
          state.catalogsCount =
            response.data.catalog_admin_view_aggregate.aggregate.count
        }
      } catch (error) {
        throw new Error(error)
      }
    },

    async getCountryByCat ({ state }) {
      try {
        const requestQuery = {
          fetchPolicy: 'network-only',
          query: countriesInCatalogs
        }

        const response = await apolloClient.query(requestQuery)
        if (response && response.data) {
          state.catalogCountries = response.data.catalog_admin_view.map(
            c => c.country
          )
        }
      } catch (error) {
        throw new Error(error)
      }
    }
  },
  mutations: {
    SET_VALIDATOR_RESULTS (state, data) {
      Vue.set(state, 'validatorResult', data)
    },
    SET_CATALOGS (state, data) {
      Vue.set(state, 'catalogs', data)
    },
    SET_CATALOG (state, data) {
      Vue.set(state, 'catalog', data)
    },
    SET_CATALOG_UPLOAD (state, data) {
      Vue.set(state, 'catalog_upload', data)
    },
    SET_CATALOG_TYPES (state, data) {
      Vue.set(state, 'catalog_types', data)
    },
    SET_LIST_CATALOG_BY_ORG (state, data) {
      Vue.set(state, 'list_catalogs', data)
    }
  },
  getters: {
    validatorResultInfo: state => state.validatorResult,
    timestamptzInfo: state => state.timestamptz,
    catalogsInfo: state => state.catalogs,
    catalogUploadInfo: state => state.catalog_upload,
    catalogInfo: state => state.catalog,
    catalogTypesInfo: state => state.catalog_types
  }
}

export default catalog



// WEBPACK FOOTER //
// ./src/store/modules/catalog.js